import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, ScanLine, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const STATUS_STYLES = {
  red: "bg-[#F50E02]/10 text-[#F50E02] border-[#F50E02]/20",
  yellow: "bg-amber-100 text-amber-800 border-amber-200",
  green: "bg-emerald-100 text-emerald-800 border-emerald-200",
  gray: "bg-[#F5F5F5] text-[#525252] border-black/10",
};

function StatusPill({ value }) {
  const key = (value || "gray").toLowerCase();
  const cls = STATUS_STYLES[key] || STATUS_STYLES.gray;
  return (
    <span
      className={`inline-flex items-center px-2 h-6 rounded-full border text-[11px] font-semibold uppercase tracking-wider ${cls}`}
      data-testid={`scan-status-${key}`}
    >
      {key}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ScanHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scan-history");
      setItems(data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load scan history"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        (s.member_name || "").toLowerCase().includes(q) ||
        (s.member_phone || "").toLowerCase().includes(q) ||
        (s.temp_number || "").toLowerCase().includes(q) ||
        (s.qr_code || "").toLowerCase().includes(q) ||
        (s.vehicle_number || "").toLowerCase().includes(q) ||
        (s.overall_status || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div data-testid="scan-history-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            Scan History
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Audit log of every mobile QR scan performed by App Users.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="h-10 border-black/10"
          data-testid="scan-history-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Refresh
        </Button>
      </header>

      <div className="p-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
            <Input
              placeholder="Search by scanner, QR, Temp No, vehicle, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-white border-black/10"
              data-testid="scan-history-search"
            />
          </div>
          <span className="text-xs text-[#525252]">
            {filteredRows.length}{" "}
            {filteredRows.length === 1 ? "scan" : "scans"}
          </span>
        </div>

        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="scan-history-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Sr.No.
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Scanned At
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Scanner
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Temp No.
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    QR Code
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Vehicle
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Cylinders
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    R / Y / G
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Overall
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-10 text-center text-[#525252]"
                    >
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-[#525252]"
                    >
                      <div className="inline-flex flex-col items-center gap-2">
                        <ScanLine className="h-6 w-6 text-[#A3A3A3]" />
                        {search ? (
                          <span>No scans match &quot;{search}&quot;.</span>
                        ) : (
                          <span>
                            No scans yet. Mobile QR scans will appear here.
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((s, idx) => {
                    const counts = s.counts || {};
                    const r = counts.red ?? 0;
                    const y = counts.yellow ?? 0;
                    const g = counts.green ?? 0;
                    return (
                      <tr
                        key={s.id}
                        className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60 align-top"
                        data-testid={`scan-row-${s.id}`}
                      >
                        <td className="px-4 py-3 text-[#525252]">{idx + 1}</td>
                        <td className="px-4 py-3 text-[#0A0A0A] whitespace-nowrap">
                          {formatDate(s.scanned_at)}
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A]">
                          <div className="font-medium">
                            {s.member_name || "—"}
                          </div>
                          <div className="text-xs text-[#525252]">
                            {s.member_phone || ""}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A] font-mono text-xs">
                          {s.temp_number || "—"}
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A] font-mono text-xs">
                          {s.qr_code || "—"}
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A]">
                          {s.vehicle_number || "—"}
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A]">
                          {s.total_cylinders ?? 0}
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A] whitespace-nowrap">
                          <span className="text-[#F50E02] font-semibold">
                            {r}
                          </span>
                          <span className="text-[#A3A3A3]"> / </span>
                          <span className="text-amber-700 font-semibold">
                            {y}
                          </span>
                          <span className="text-[#A3A3A3]"> / </span>
                          <span className="text-emerald-700 font-semibold">
                            {g}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill value={s.overall_status} />
                        </td>
                        <td className="px-4 py-3 text-[#0A0A0A] max-w-xs">
                          <span className="line-clamp-2">
                            {s.message || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
